/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */

import { useRouter } from 'next/router'
import slugify from 'slugify'
import { useState } from 'react'
import classNames from 'classnames'
import Cookies from 'js-cookie'
import { useInfiniteQuery } from 'react-query'
import SpinnerLoader from '@/components/Common/header/spinner-loader'
import { StencilCDNImage } from '@/components/Common/StencilImage'
import { TINStocksTransactionsData } from '@/components/Stocks/types/TINStocksTransactionsData'
import { fetchRequest } from '@/root/utils/network-adapter/fetch-request'
import formatNumberToString, {
  formatNumberToINRCommaSeparatedString,
} from '@/root/utils/common/numbers/format-number-to-string'
import { vyaparApiBaseUrl } from '@/root/api/apiConstants'
import INStocksTransactionsFilterModal from './Dashboard/INStocksTransactionsFilterModal'
import { getStatusColorClassName } from '@/components/USStocks/USStocksWalletHistory/getStatusLogo'
import CancelOrderModal, {
  CancelOrderModalProps,
} from './Dashboard/CancelOrderModal'

export default function INStocksTransactions({
  member_id,
  apiExtraParams = '',
}: {
  member_id?: number | null
  apiExtraParams?: string
}) {
  const router = useRouter()

  const type = (router.query.type as string | undefined)?.toLowerCase()

  const [showFilterModal, setShowFilterModal] = useState(false)

  const [filter, setFilter] = useState<{ type: string }>({ type: 'all' })

  const modeFromParams =
    type === 'sips' || type === 'cnc' || type === 'intraday' ? type : 'cnc'

  const [mode, setMode] = useState<'sips' | 'cnc' | 'intraday'>(modeFromParams)

  const [showCancelOrderModal, setShowCancelOrderModal] =
    useState<CancelOrderModalProps | null>(null)

  const {
    isSuccess,
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery<TINStocksTransactionsData>(
    [
      'instocksusertransactionspaginated',
      member_id,
      mode,
      filter.type,
      apiExtraParams,
    ],
    async ({
      pageParam = `?transaction_type=all&transaction_status=${
        filter.type
      }&todays_transaction_offset=0&historic_transaction_offset=0&limit=10&transaction_tabs_req=false&tab-id=${mode}${
        mode === 'sips' ? `&sip_offset=0&sip_status=${filter.type}` : ''
      }${apiExtraParams}&response_format=json`,
    }) => {
      const url = `${vyaparApiBaseUrl}/portfolio/transactions${pageParam}${
        member_id ? `&member_id=${member_id}` : ''
      }`

      const token = Cookies.get('token')

      const response = await fetchRequest({
        url,
        config: {
          headers: {
            Authorization: `Bearer ${token}`,
            Platform: 'web',
          },
        },
      })

      return response
    },
    {
      // query params received from backend to send to the next paginated call
      getNextPageParam(lastPage) {
        return lastPage.data?.metadata?.paginate_url
      },
      retry: 3,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  )
  const transactionsDataAggregated = data?.pages
    .flatMap(page => {
      if (page.data?.transactions) return page.data.transactions
      return null
    })
    .filter(Boolean)

  const sipDataAggregated = data?.pages
    .flatMap(page => {
      if (page.data?.sips) return page.data.sips
      return null
    })
    .filter(Boolean)

  const intradayDataAggregated = data?.pages
    .flatMap(page => {
      if (page.data?.intraday_transactions)
        return page.data.intraday_transactions
      return null
    })
    .filter(Boolean)

  const showCancelOrderCtaCNC = transactionsDataAggregated?.some(
    item => item?.transaction_status === 'PENDING',
  )

  const showCancelOrderCtaIntraday = intradayDataAggregated?.some(
    item => item?.transaction_status === 'PENDING',
  )

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-10">
        <SpinnerLoader />
      </div>
    )
  } else if (isSuccess) {
    return (
      <div className="flex flex-col gap-4 p-2">
        {showFilterModal && (
          <INStocksTransactionsFilterModal
            showModal={showFilterModal}
            mode={mode}
            onClose={() => {
              setShowFilterModal(false)
            }}
            setFilter={setFilter}
            filter={filter}
          />
        )}
        {showCancelOrderModal?.product && (
          <CancelOrderModal
            ind_stock_id={showCancelOrderModal.ind_stock_id}
            product={showCancelOrderModal.product}
            showModal={!!showCancelOrderModal.product}
            closeModal={() => setShowCancelOrderModal(null)}
            refetch={refetch}
          />
        )}
        <div className="flex w-full items-center justify-between">
          <div className="flex gap-2">
            <button
              className={classNames(
                'shadow-one border-1 rounded-full px-4 py-1 text-sm transition-colors',
                {
                  'bg-brand-blue-azureRadiance  text-brand-white ':
                    mode === 'cnc',
                  'bg-brand-white text-brand-blue-azureRadiance ':
                    mode !== 'cnc',
                },
              )}
              onClick={() => {
                setMode('cnc')
                setFilter({ type: 'all' })
              }}
            >
              Delivery
            </button>

            <button
              className={classNames(
                'shadow-one border-1 rounded-full px-4 py-1 text-sm transition-colors',
                {
                  'bg-brand-blue-azureRadiance  text-brand-white':
                    mode === 'sips',
                  'bg-brand-white text-brand-blue-azureRadiance':
                    mode !== 'sips',
                },
              )}
              onClick={() => {
                setMode('sips')
                setFilter({ type: 'all' })
              }}
            >
              SIP
            </button>
            <button
              className={classNames(
                'shadow-one border-1 rounded-full px-4 py-1 text-sm transition-colors',
                {
                  'bg-brand-blue-azureRadiance  text-brand-white ':
                    mode === 'intraday',
                  'bg-brand-white text-brand-blue-azureRadiance ':
                    mode !== 'intraday',
                },
              )}
              onClick={() => {
                setMode('intraday')
                setFilter({ type: 'all' })
              }}
            >
              Intraday
            </button>
          </div>

          <button
            className={classNames(
              'shadow-one border-1 rounded-full px-4 py-1 text-sm transition-colors',
              {
                ' bg-brand-white text-brand-blue-azureRadiance ':
                  filter.type === 'all',
                'bg-brand-blue-azureRadiance text-brand-white':
                  filter.type !== 'all',
              },
            )}
            onClick={() => setShowFilterModal(true)}
          >
            Filter
          </button>
        </div>
        {mode === 'cnc' && showCancelOrderCtaCNC && (
          <div
            className="text-brand-blue-azureRadiance bg-brand-white shadow-one flex cursor-pointer items-center gap-2 self-start rounded-xl px-4 py-1 text-sm"
            onClick={() => {
              setShowCancelOrderModal({ product: mode, ind_stock_id: null })
            }}
          >
            <StencilCDNImage
              src="https://indcdn.indmoney.com/public/images/ExitPositionsIcon.png"
              width={15}
              height={15}
            />
            Cancel Open Orders
          </div>
        )}
        {mode === 'intraday' && showCancelOrderCtaIntraday && (
          <div
            className="text-brand-blue-azureRadiance bg-brand-white shadow-one flex cursor-pointer items-center gap-2 self-start rounded-xl px-4 py-1 text-sm"
            onClick={() => {
              setShowCancelOrderModal({ product: mode, ind_stock_id: null })
            }}
          >
            <StencilCDNImage
              src="https://indcdn.indmoney.com/public/images/ExitPositionsIcon.png"
              width={15}
              height={15}
            />
            Cancel Open Orders
          </div>
        )}
        {mode === 'cnc' &&
          (transactionsDataAggregated?.length ? (
            transactionsDataAggregated.map((item, idx) => {
              if (item) {
                return (
                  <div
                    className="shadow-one flex w-full cursor-pointer items-center gap-2  rounded-xl bg-white p-2 lg:p-5 "
                    key={idx}
                    onClick={() => {
                      if (
                        (item.vendor_order_id || item.vyapar_order_id) &&
                        item.name
                      ) {
                        void router.push(
                          `/np-payment/payment-status?source=vyapar&statusId=${
                            item.vendor_order_id
                          }&stock_slug=${slugify(
                            item.name.toLowerCase(),
                          )}-share-price&statusId2=${item.vyapar_order_id}`,
                        )
                      }
                    }}
                  >
                    <div className="flex lg:w-1/12 lg:items-center">
                      {item.logo ? (
                        <StencilCDNImage
                          src={item.logo}
                          width={50}
                          height={50}
                          className="rounded-full"
                        />
                      ) : (
                        <div className="bg-brand-grey-light flex h-12 w-12 items-center justify-center rounded-full text-xs">
                          {item.name?.slice(0, 4).toUpperCase()}
                        </div>
                      )}
                    </div>

                    {item.name && (
                      <div className="hover:text-brand-blue-azureRadiance ml-2 flex w-3/12 cursor-pointer items-center font-medium">
                        <span
                          className="overflow-hidden 
                    text-wrap
                    "
                        >
                          {item.name}
                        </span>
                      </div>
                    )}
                    {item.transaction_type &&
                      typeof item.quantity === 'number' && (
                        <div className="flex w-2/12 flex-col items-center justify-center ">
                          {item.transaction_type.toLowerCase() === 'buy' && (
                            <span className="text-brand-grey-base text-sm">
                              Buy (Units)
                            </span>
                          )}
                          {item.transaction_type.toLowerCase() === 'sell' && (
                            <span className="text-brand-grey-base text-sm">
                              Sell (Units)
                            </span>
                          )}
                          <span className="text-brand-grey-dark">
                            {item.quantity}
                          </span>
                        </div>
                      )}
                    {item.trade_date && (
                      <div className="flex w-2/12 flex-col items-start items-center justify-center">
                        <span className="text-brand-grey-base text-sm">
                          Trade Date
                        </span>

                        <span>{item.trade_date}</span>
                      </div>
                    )}
                    {typeof item.price === 'number' && (
                      <div className="w-3/12 lg:w-2/12 ">
                        <div className="text-brand-grey-base text-center text-sm lg:text-left">
                          Price
                        </div>

                        <div className="overflow-x-hidden text-ellipsis whitespace-nowrap text-center lg:text-left">
                          {formatNumberToINRCommaSeparatedString(item.price)}
                        </div>

                        {item.transaction_status && (
                          <div className="text-center lg:hidden">
                            <span
                              className={classNames(
                                ' rounded-xl p-1 text-center text-[10px] lg:text-sm',
                                getStatusColorClassName(
                                  item.transaction_status,
                                ),
                              )}
                            >
                              {item.transaction_status.charAt(0).toUpperCase() +
                                item.transaction_status.slice(1)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {item.transaction_status && (
                      <div className="hidden w-2/12 text-center lg:block">
                        <span
                          className={classNames(
                            'w-25 rounded-xl px-4 py-1 text-center text-sm',
                            getStatusColorClassName(item.transaction_status),
                          )}
                        >
                          {item.transaction_status.charAt(0).toUpperCase() +
                            item.transaction_status.slice(1)}
                        </span>
                      </div>
                    )}
                  </div>
                )
              }
              return null
            })
          ) : (
            <div className="bg-brand-white shadow-one flex w-full flex-col items-center justify-center gap-2 rounded-xl p-5">
              <StencilCDNImage
                src="https://indcdn.indmoney.com/public/images/indstocksdashboardnostocksicon.png"
                width={50}
                height={50}
              />
              <p className="text-brand-grey-base text-center">
                You have no CNC transactions{' '}
              </p>
            </div>
          ))}
        {mode === 'sips' &&
          (sipDataAggregated?.length ? (
            sipDataAggregated.map((item, idx) => {
              if (item) {
                return (
                  <div
                    className="shadow-one flex w-full cursor-pointer items-center gap-2  rounded-xl bg-white p-2 lg:p-5 "
                    key={idx}
                    onClick={() => {
                      if (
                        (item.sip_ui_status?.toLowerCase() === 'registered' ||
                          item.sip_ui_status?.toLowerCase() === 'paused') &&
                        item.sip_id
                      ) {
                        void router.push(
                          `/investments/stocks/sip/${item.sip_id}`,
                        )
                      } else if (item.sip_id && item.name) {
                        void router.push(
                          `/np-payment/payment-status?source=in-stocks-sip&statusId=${
                            item.sip_id
                          }&stock_slug=${slugify(
                            item.name.toLowerCase(),
                          )}-share-price`,
                        )
                      }
                    }}
                  >
                    <div className="flex lg:w-1/12 lg:items-center">
                      {item.logo ? (
                        <StencilCDNImage
                          src={item.logo}
                          width={50}
                          height={50}
                          className="rounded-full"
                        />
                      ) : (
                        <div className="bg-brand-grey-light flex h-12 w-12 items-center justify-center rounded-full text-xs">
                          {item.name?.slice(0, 4).toUpperCase()}
                        </div>
                      )}
                    </div>

                    {item.name && (
                      <div className="hover:text-brand-blue-azureRadiance ml-2 flex w-3/12 cursor-pointer items-center font-medium">
                        <span
                          className="overflow-hidden 
                    text-wrap
                    "
                        >
                          {item.name}
                        </span>
                      </div>
                    )}
                    {item.sip_type && typeof item.quantity === 'number' && (
                      <div className="flex w-2/12 flex-col items-center justify-center ">
                        <span className="text-brand-grey-base text-sm">
                          SIP Qty
                        </span>
                        <span className="text-brand-grey-dark">
                          {item.quantity}
                        </span>
                      </div>
                    )}
                    {item.created_at && (
                      <div className="flex w-2/12 flex-col items-center justify-center">
                        <span className="text-brand-grey-base text-sm">
                          SIP Date
                        </span>

                        <span>{item.created_at}</span>
                      </div>
                    )}
                    {/* {item.sip_frequency && (
                      <div className="flex flex-col justify-center items-center w-2/12">
                        <span className="text-brand-grey-base text-sm">
                          SIP Date
                        </span>

                        <span>{item.sip_frequency}</span>
                      </div>
                    )} */}
                    {typeof item.sip_frequency === 'string' && (
                      <div className="w-3/12 lg:w-2/12 ">
                        <div className="text-brand-grey-base text-center text-sm lg:text-left">
                          Frequency
                        </div>

                        <div className="overflow-x-hidden text-ellipsis whitespace-nowrap text-center lg:text-left">
                          {item.sip_frequency.charAt(0).toUpperCase() +
                            item.sip_frequency.slice(1)}
                        </div>

                        {item.sip_ui_status && (
                          <div className="text-center lg:hidden">
                            <span
                              className={classNames(
                                ' rounded-xl p-1 text-center text-[10px] lg:text-sm',
                                getStatusColorClassName(item.sip_ui_status),
                              )}
                            >
                              {item.sip_ui_status.charAt(0).toUpperCase() +
                                item.sip_ui_status.slice(1)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {item.sip_ui_status && (
                      <div className="hidden w-2/12 text-center lg:block">
                        <span
                          className={classNames(
                            'w-25 rounded-xl px-4 py-1 text-center text-sm',
                            getStatusColorClassName(item.sip_ui_status),
                          )}
                        >
                          {item.sip_ui_status.charAt(0).toUpperCase() +
                            item.sip_ui_status.slice(1)}
                        </span>
                      </div>
                    )}
                  </div>
                )
              }
              return null
            })
          ) : (
            <div className="bg-brand-white shadow-one flex w-full flex-col items-center justify-center gap-2 rounded-xl p-5">
              <StencilCDNImage
                src="https://indcdn.indmoney.com/public/images/indstocksdashboardnostocksicon.png"
                width={50}
                height={50}
              />
              <p className="text-brand-grey-base text-center">
                You have no SIP transactions{' '}
              </p>
            </div>
          ))}
        {mode === 'intraday' &&
          (intradayDataAggregated?.length ? (
            intradayDataAggregated.map((item, idx) => {
              if (item) {
                return (
                  <div
                    className="shadow-one flex w-full cursor-pointer items-center gap-2  rounded-xl bg-white p-2 lg:p-5 "
                    key={idx}
                    onClick={() => {
                      if (
                        item.name &&
                        (item.vendor_order_id || item.vyapar_order_id)
                      ) {
                        void router.push(
                          `/np-payment/payment-status?source=vyapar&statusId=${
                            item.vendor_order_id
                          }&stock_slug=${slugify(
                            item.name.toLowerCase(),
                          )}-share-price&statusId2=${item.vyapar_order_id}`,
                        )
                      }
                    }}
                  >
                    <div className="flex lg:w-1/12 lg:items-center">
                      {item.logo ? (
                        <StencilCDNImage
                          src={item.logo}
                          width={50}
                          height={50}
                          className="rounded-full"
                        />
                      ) : (
                        <div className="bg-brand-grey-light flex h-12 w-12 items-center justify-center rounded-full text-xs">
                          {item.name?.slice(0, 4).toUpperCase()}
                        </div>
                      )}
                    </div>

                    {item.name && (
                      <div className="hover:text-brand-blue-azureRadiance ml-2 flex w-3/12 cursor-pointer items-center font-medium">
                        <span
                          className="overflow-hidden 
                  text-wrap
                  "
                        >
                          {item.name}
                        </span>
                      </div>
                    )}
                    {item.transaction_type &&
                      typeof item.quantity === 'number' && (
                        <div className="flex w-2/12 flex-col items-center justify-center ">
                          {item.transaction_type.toLowerCase() === 'buy' && (
                            <span className="text-brand-grey-base text-sm">
                              Buy (Units)
                            </span>
                          )}
                          {item.transaction_type.toLowerCase() === 'sell' && (
                            <span className="text-brand-grey-base text-sm">
                              Sell (Units)
                            </span>
                          )}
                          <span className="text-brand-grey-dark">
                            {item.quantity}
                          </span>
                        </div>
                      )}
                    {item.trade_date && (
                      <div className="flex w-2/12 flex-col items-start items-center justify-center">
                        <span className="text-brand-grey-base text-sm">
                          Trade Date
                        </span>

                        <span>{item.trade_date}</span>
                      </div>
                    )}
                    {(typeof item.price === 'number' || item.trigger_price) && (
                      <div className="w-3/12 lg:w-2/12 ">
                        <div className="text-brand-grey-base text-center text-sm lg:text-left">
                          {item.trigger_price &&
                          item.order_type === 'STOP_LOSS_MARKET'
                            ? 'Trigger Price'
                            : 'Price'}
                        </div>

                        <div className="overflow-x-hidden text-ellipsis whitespace-nowrap text-center lg:text-left">
                          {item.trigger_price &&
                          item.order_type === 'STOP_LOSS_MARKET'
                            ? `₹${formatNumberToString(
                                Number(item.trigger_price.toFixed(2)),
                                false,
                              )}`
                            : formatNumberToINRCommaSeparatedString(item.price)}
                        </div>

                        {item.transaction_status && (
                          <div className="text-center lg:hidden">
                            <span
                              className={classNames(
                                ' rounded-xl p-1 text-center text-[10px] lg:text-sm',
                                getStatusColorClassName(
                                  item.transaction_status,
                                ),
                              )}
                            >
                              {item.transaction_status.charAt(0).toUpperCase() +
                                item.transaction_status.slice(1)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {item.transaction_status && (
                      <div className=" hidden min-w-fit text-center lg:block">
                        <span
                          className={classNames(
                            'w-25 rounded-xl px-4 py-1 text-center text-sm',
                            getStatusColorClassName(item.transaction_status),
                          )}
                        >
                          {item.transaction_status.charAt(0).toUpperCase() +
                            item.transaction_status.slice(1)}
                        </span>
                      </div>
                    )}
                  </div>
                )
              }
              return null
            })
          ) : (
            <div className="bg-brand-white shadow-one flex w-full flex-col items-center justify-center gap-2 rounded-xl p-5">
              <StencilCDNImage
                src="https://indcdn.indmoney.com/public/images/indstocksdashboardnostocksicon.png"
                width={50}
                height={50}
              />
              <p className="text-brand-grey-base text-center">
                You have no intraday transactions{' '}
              </p>
            </div>
          ))}
        {isFetchingNextPage && <SpinnerLoader />}
        {hasNextPage &&
          !isFetchingNextPage &&
          ((!!transactionsDataAggregated?.length && mode === 'cnc') ||
            (!!sipDataAggregated?.length && mode === 'sips') ||
            (!!intradayDataAggregated?.length && mode === 'intraday')) && (
            <span
              className="text-brand-blue-azureRadiance w-fit cursor-pointer self-center text-lg"
              onClick={() => fetchNextPage()}
            >
              Load More
            </span>
          )}
      </div>
    )
  } else {
    return (
      <div className="flex items-center justify-center">
        <div className="text-center">
          <p className="text-16px">
            Something went wrong while fetching your transactions, please try
            again in a while!
          </p>
        </div>
      </div>
    )
  }
}
